import React             from "react";
import { Box, AppBar as MuiAppBar, Toolbar, Typography } from "@mui/material";
import { useTheme }      from "@mui/material/styles";
import { useNavigate }   from "react-router-dom";

import WordMark          from "../../common/WordMark";
import ThemeToggleButton from "../../common/ThemeToggleButton";
import NotificationBell  from "./components/NotificationBell";
import UserMenu          from "./components/UserMenu";
import NavTabs           from "./components/NavTabs";

const ON_BAR = {
  text:    "rgba(255,255,255,0.90)",
  text2:   "rgba(255,255,255,0.55)",
  text3:   "rgba(255,255,255,0.30)",
  border:  "rgba(255,255,255,0.10)",
  border2: "rgba(255,255,255,0.14)",
  hover:   "rgba(255,255,255,0.07)",
  active:  "rgba(255,255,255,0.11)",
};

const PSIAppBar = ({
  tabs       = [],
  activeTab,
  onTabChange,
  subtitle   = "RESOURCE ALLOCATION",
  macBadge,
}) => {
  const theme    = useTheme();
  const t        = theme.custom;
  const navigate = useNavigate();

  return (
    <MuiAppBar
      position="sticky"
      elevation={0}
      sx={{
        background:   t.appBar,
        borderBottom: `1px solid ${ON_BAR.border}`,
        boxShadow:    "none",
      }}
    >
      {/* Main toolbar row */}
      <Toolbar
        disableGutters
        sx={{
          minHeight:  "52px !important",
          height:     "52px",
          padding:    "0 0 0 20px",
          display:    "flex",
          alignItems: "stretch",
          gap:        0,
        }}
      >
        {/* LEFT — Wordmark + subtitle
            On small screens subtitle is hidden to save space */}
        <Box sx={{
          display:    "flex",
          alignItems: "center",
          gap:        "12px",
          pr:         "16px",
          flexShrink: 0,
        }}>
          <Box sx={{
            "& *":    { color: `${ON_BAR.text} !important` },
            "& span": { color: `${t.gold} !important` },
          }}>
            <WordMark size="20px" />
          </Box>

          {/* Subtitle — hidden below md breakpoint */}
          <Box
            component="span"
            sx={{
              display: { xs: "none", md: "block" },
              fontFamily:    "'Inter', 'DM Mono', monospace",
              fontSize:      "8.5px",
              fontWeight:    500,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              color:         ON_BAR.text3,
              userSelect:    "none",
              whiteSpace:    "nowrap",
              borderLeft:    `1px solid ${ON_BAR.border2}`,
              paddingLeft:   "12px",
            }}
          >
            {subtitle}
          </Box>
        </Box>

        {/* CENTER — Nav tabs
            Scrollable on small screens so tabs never get cut off */}
        <Box sx={{
          flex:       1,
          display:    "flex",
          alignItems: "stretch",
          overflow:   "hidden",
          minWidth:   0,
        }}>
          <NavTabs
            tabs={tabs}
            activeTab={activeTab}
            onChange={onTabChange}
            onBar={ON_BAR}
          />
        </Box>

        {/* RIGHT — MAC badge + utility icons */}
        <Box sx={{
          display:    "flex",
          alignItems: "center",
          gap:        "2px",
          padding:    "0 0 0 8px",
          borderLeft: `1px solid ${ON_BAR.border}`,
          flexShrink: 0,
        }}>
          {/* MAC badge — hidden below sm, shown from sm up */}
          {macBadge && (
            <Box sx={{
              display:    { xs: "none", sm: "flex" },
              alignItems: "center",
              height:     "100%",
              padding:    "0 12px",
              borderRight: `1px solid ${ON_BAR.border}`,
            }}>
              <Typography sx={{
                fontFamily:    "'DM Mono', monospace",
                fontSize:      "9px",
                fontWeight:    600,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color:         t.gold,
                background:    `${t.gold}20`,
                border:        `1px solid ${t.gold}40`,
                borderRadius:  "3px",
                padding:       "2px 8px",
                userSelect:    "none",
                whiteSpace:    "nowrap",
              }}>
                {macBadge}
              </Typography>
            </Box>
          )}

          <Box
            onClick={() => navigate("/resources")}
            sx={{
              display: "flex", alignItems: "center",
              height: "100%", padding: "0 10px",
              borderRight: `1px solid ${ON_BAR.border}`,
              cursor: "pointer",
              "&:hover": { opacity: 0.75 },
            }}
          >
            <Typography sx={{
              fontFamily: "'DM Mono', monospace",
              fontSize: "9px", fontWeight: 600,
              letterSpacing: "0.1em", textTransform: "uppercase",
              color: ON_BAR.text2,
              border: `1px solid ${ON_BAR.border2}`,
              borderRadius: "3px", padding: "2px 8px",
              whiteSpace: "nowrap",
            }}>
              Master Datasets
            </Typography>
          </Box>

          <Box
            onClick={() => navigate("/dossier")}
            sx={{
              display: "flex", alignItems: "center",
              height: "100%", padding: "0 10px",
              borderRight: `1px solid ${ON_BAR.border}`,
              cursor: "pointer",
              "&:hover": { opacity: 0.75 },
            }}
          >
            <Typography sx={{
              fontFamily: "'DM Mono', monospace",
              fontSize: "9px", fontWeight: 600,
              letterSpacing: "0.1em", textTransform: "uppercase",
              color: ON_BAR.text2,
              border: `1px solid ${ON_BAR.border2}`,
              borderRadius: "3px", padding: "2px 8px",
              whiteSpace: "nowrap",
            }}>
              PSI360 Dossier
            </Typography>
          </Box>

          <Box
            component="a"
            href="https://forms.cloud.microsoft/r/WzYx7hmafM"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: "flex", alignItems: "center",
              height: "100%", padding: "0 10px",
              borderRight: `1px solid ${ON_BAR.border}`,
              textDecoration: "none", cursor: "pointer",
              "&:hover": { opacity: 0.75 },
            }}
          >
            <Typography sx={{
              fontFamily: "'DM Mono', monospace",
              fontSize: "9px", fontWeight: 600,
              letterSpacing: "0.1em", textTransform: "uppercase",
              color: t.accent,
              border: `1px solid ${t.accent}44`,
              borderRadius: "3px", padding: "2px 8px",
              whiteSpace: "nowrap",
            }}>
              Raise a Request
            </Typography>
          </Box>

          <ThemeToggleButton onBar={ON_BAR} />
          <NotificationBell  onBar={ON_BAR} />

          <Box sx={{
            width:      "1px",
            height:     "18px",
            background: ON_BAR.border2,
            mx:         "4px",
            flexShrink: 0,
          }} />

          <UserMenu onBar={ON_BAR} />
        </Box>
      </Toolbar>
    </MuiAppBar>
  );
};

export default PSIAppBar;